import * as React from 'react';
import { v4 as uuid } from 'uuid';

import { DataSourceTable } from 'src/constants/dataSourceTable';
import {
  DataSourceType,
  SAMPLE_MUSIC_DATA_SOURCE_TYPE,
} from 'src/constants/dataSourceTypeDescription';
import { VirtualGraphSourceConfig } from 'src/containers/CreateProjectOverlay/types';
import {
  VgCustomQuery,
  VgTableMetadata,
} from 'src/containers/ProjectNewResourceDialog/hooks/useVirtualGraphDialogState';
import { AdvancedOption } from 'src/containers/ProjectVirtualGraphPreviewDialog/components/AdvancedOptions';
import { DataSource } from 'src/store/state/dataSources';
import {
  ProjectVirtualGraphOptions,
  defaultProjectVirtualGraphOptions,
} from 'src/store/state/projects';
import { useSampleMusicDataSource } from 'src/utils/hooks/useSampleMusicDataSource';

type UseVgConfigStateArgs = {
  virtualGraphConfigs: VirtualGraphSourceConfig[];
  setVirtualGraphConfigs: React.Dispatch<
    React.SetStateAction<VirtualGraphSourceConfig[]>
  >;
  setIsAddDataDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDrawerFullSize: React.Dispatch<React.SetStateAction<boolean>>;
  setHeaderBackButton: React.Dispatch<
    React.SetStateAction<(() => void) | null>
  >;
};

export const useVgConfigState = ({
  virtualGraphConfigs,
  setVirtualGraphConfigs,
  setIsAddDataDrawerOpen,
  setDrawerFullSize,
  setHeaderBackButton,
}: UseVgConfigStateArgs) => {
  const [isVgConfigDrawerOpen, setIsVgConfigDrawerOpen] = React.useState(false);

  // Temporary VG state for configuration drawer
  const [pendingVgDataSource, setPendingVgDataSource] =
    React.useState<DataSource | null>(null);
  const [pendingVgDataSourceType, setPendingVgDataSourceType] = React.useState<
    string | null
  >(null);
  const [pendingVgCheckedTables, setPendingVgCheckedTables] = React.useState<
    DataSourceTable[]
  >([]);
  const [pendingVgTableMetadata, setPendingVgTableMetadata] = React.useState<
    Record<string, VgTableMetadata>
  >({});
  const [pendingVgCustomSqlQueries, setPendingVgCustomSqlQueries] =
    React.useState<VgCustomQuery[]>([]);
  const [pendingVgOptions, setPendingVgOptions] =
    React.useState<ProjectVirtualGraphOptions>(
      defaultProjectVirtualGraphOptions
    );
  const [pendingVgAdvancedOptions, setPendingVgAdvancedOptions] =
    React.useState<AdvancedOption[]>([{ property: '', value: '' }]);
  const [pendingVgPreviewItem, setPendingVgPreviewItem] = React.useState<{
    type: 'table' | 'query';
    name: string;
  } | null>(null);
  const [pendingVgConfigId, setPendingVgConfigId] = React.useState<
    string | null
  >(null);

  // Footer options state for VG config drawer
  const [vgFooterOptions, setVgFooterOptions] = React.useState<{
    onCancel?: () => void;
    onNext?: () => void;
    nextLabel?: string;
    isNextDisabled?: boolean;
  }>({});

  const {
    createOrRefresh: createOrRefreshSampleDataSource,
    isLoading: isSampleDataSourceLoading,
    existingDataSource: existingSampleDataSource,
  } = useSampleMusicDataSource();

  // Flag to track when we want to save the VG config after state updates
  const [wantToSaveVgConfig, setWantToSaveVgConfig] = React.useState(false);

  const resetPendingVgState = React.useCallback(() => {
    setPendingVgDataSource(null);
    setPendingVgDataSourceType(null);
    setPendingVgCheckedTables([]);
    setPendingVgTableMetadata({});
    setPendingVgCustomSqlQueries([]);
    setPendingVgOptions(defaultProjectVirtualGraphOptions);
    setPendingVgAdvancedOptions([{ property: '', value: '' }]);
    setPendingVgPreviewItem(null);
    setPendingVgConfigId(null);
  }, []);

  const handleCloseVgConfigDrawer = React.useCallback(() => {
    setIsVgConfigDrawerOpen(false);
    setDrawerFullSize(false);
    setHeaderBackButton(null);
    resetPendingVgState();
  }, [setDrawerFullSize, setHeaderBackButton, resetPendingVgState]);

  const saveVgConfig = React.useCallback(() => {
    if (!pendingVgDataSource || !pendingVgDataSourceType) {
      return false;
    }

    const hasSelections =
      pendingVgCheckedTables.length > 0 || pendingVgCustomSqlQueries.length > 0;
    if (!hasSelections) {
      return false;
    }

    const newConfig: VirtualGraphSourceConfig = {
      id: pendingVgConfigId || uuid(),
      dataSource: pendingVgDataSource,
      dataSourceType: pendingVgDataSourceType,
      checkedTables: pendingVgCheckedTables,
      tableMetadata: pendingVgTableMetadata,
      customSqlQueries: pendingVgCustomSqlQueries,
      virtualGraphOptions: pendingVgOptions,
      advancedVgOptions: pendingVgAdvancedOptions,
    };

    setVirtualGraphConfigs((prev) => {
      if (pendingVgConfigId) {
        return prev.map((config) =>
          config.id === pendingVgConfigId ? newConfig : config
        );
      }
      return [...prev, newConfig];
    });

    handleCloseVgConfigDrawer();
    setIsAddDataDrawerOpen(false);
    return true;
  }, [
    pendingVgDataSource,
    pendingVgDataSourceType,
    pendingVgCheckedTables,
    pendingVgTableMetadata,
    pendingVgCustomSqlQueries,
    pendingVgOptions,
    pendingVgAdvancedOptions,
    pendingVgConfigId,
    setVirtualGraphConfigs,
    setIsAddDataDrawerOpen,
    handleCloseVgConfigDrawer,
  ]);

  React.useEffect(() => {
    if (wantToSaveVgConfig && pendingVgDataSource) {
      setWantToSaveVgConfig(false);
      saveVgConfig();
    }
  }, [wantToSaveVgConfig, pendingVgDataSource, saveVgConfig]);

  const handleSelectDataSourceType = React.useCallback(
    async (dataSourceType: DataSourceType) => {
      if (dataSourceType === SAMPLE_MUSIC_DATA_SOURCE_TYPE) {
        const dataSource = await createOrRefreshSampleDataSource();
        if (dataSource) {
          setPendingVgDataSource(existingSampleDataSource || dataSource);
          setPendingVgDataSourceType(SAMPLE_MUSIC_DATA_SOURCE_TYPE);
          setIsVgConfigDrawerOpen(true);
          setDrawerFullSize(true);
        }
        return;
      }

      setPendingVgDataSourceType(dataSourceType);
      setIsVgConfigDrawerOpen(true);
    },
    [
      createOrRefreshSampleDataSource,
      existingSampleDataSource,
      setDrawerFullSize,
    ]
  );

  const handleRemoveVgTable = React.useCallback(
    (configId: string, tableName: string) => {
      setVirtualGraphConfigs((prev) =>
        prev
          .map((config) => {
            if (config.id !== configId) {
              return config;
            }
            const newTables = config.checkedTables.filter(
              (t) => t.name !== tableName
            );
            const newMetadata = { ...config.tableMetadata };
            delete newMetadata[tableName];
            if (
              newTables.length === 0 &&
              config.customSqlQueries.length === 0
            ) {
              return null;
            }
            return {
              ...config,
              checkedTables: newTables,
              tableMetadata: newMetadata,
            };
          })
          .filter(
            (config): config is VirtualGraphSourceConfig => config !== null
          )
      );
    },
    [setVirtualGraphConfigs]
  );

  const handleRemoveVgQuery = React.useCallback(
    (configId: string, queryName: string) => {
      setVirtualGraphConfigs((prev) =>
        prev
          .map((config) => {
            if (config.id !== configId) {
              return config;
            }
            const newQueries = config.customSqlQueries.filter(
              (q) => q.name !== queryName
            );
            if (config.checkedTables.length === 0 && newQueries.length === 0) {
              return null;
            }
            return {
              ...config,
              customSqlQueries: newQueries,
            };
          })
          .filter(
            (config): config is VirtualGraphSourceConfig => config !== null
          )
      );
    },
    [setVirtualGraphConfigs]
  );

  const loadConfigIntoPendingState = React.useCallback(
    (config: VirtualGraphSourceConfig) => {
      setPendingVgConfigId(config.id);
      setPendingVgDataSource(config.dataSource);
      setPendingVgDataSourceType(config.dataSourceType);
      setPendingVgCheckedTables(config.checkedTables);
      setPendingVgTableMetadata(config.tableMetadata);
      setPendingVgCustomSqlQueries(config.customSqlQueries);
      setPendingVgOptions(config.virtualGraphOptions);
      setPendingVgAdvancedOptions(config.advancedVgOptions);
    },
    []
  );

  const handleViewVgTablePreview = React.useCallback(
    (config: VirtualGraphSourceConfig, tableName: string) => {
      loadConfigIntoPendingState(config);
      setPendingVgPreviewItem({ type: 'table', name: tableName });
      setIsVgConfigDrawerOpen(true);
      setDrawerFullSize(true);
    },
    [setDrawerFullSize, loadConfigIntoPendingState]
  );

  const handleViewVgQueryPreview = React.useCallback(
    (config: VirtualGraphSourceConfig, queryName: string) => {
      loadConfigIntoPendingState(config);
      setPendingVgPreviewItem({ type: 'query', name: queryName });
      setIsVgConfigDrawerOpen(true);
      setDrawerFullSize(true);
    },
    [setDrawerFullSize, loadConfigIntoPendingState]
  );

  const handleAddVgButtonClick = React.useCallback(() => {
    vgFooterOptions.onNext?.();
    if (pendingVgDataSource) {
      setWantToSaveVgConfig(false);
      saveVgConfig();
    } else {
      setWantToSaveVgConfig(true);
    }
  }, [vgFooterOptions, pendingVgDataSource, saveVgConfig]);

  // When closing from a preview of an existing config, save edits first.
  const handleSaveAndCloseVgConfigDrawer = React.useCallback(() => {
    if (pendingVgConfigId) {
      if (!saveVgConfig()) {
        handleCloseVgConfigDrawer();
      }
    } else {
      handleCloseVgConfigDrawer();
    }
  }, [pendingVgConfigId, saveVgConfig, handleCloseVgConfigDrawer]);

  const totalVgResources = virtualGraphConfigs.reduce(
    (sum, config) =>
      sum + config.checkedTables.length + config.customSqlQueries.length,
    0
  );

  return {
    isVgConfigDrawerOpen,
    isSampleDataSourceLoading,
    pendingVgDataSource,
    setPendingVgDataSource,
    pendingVgDataSourceType,
    setPendingVgDataSourceType,
    pendingVgCheckedTables,
    setPendingVgCheckedTables,
    pendingVgTableMetadata,
    setPendingVgTableMetadata,
    pendingVgCustomSqlQueries,
    setPendingVgCustomSqlQueries,
    pendingVgOptions,
    setPendingVgOptions,
    pendingVgAdvancedOptions,
    setPendingVgAdvancedOptions,
    pendingVgPreviewItem,
    setPendingVgPreviewItem,
    vgFooterOptions,
    setVgFooterOptions,
    totalVgResources,
    resetPendingVgState,
    handleCloseVgConfigDrawer,
    handleSaveAndCloseVgConfigDrawer,
    saveVgConfig,
    handleSelectDataSourceType,
    handleRemoveVgTable,
    handleRemoveVgQuery,
    handleViewVgTablePreview,
    handleViewVgQueryPreview,
    handleAddVgButtonClick,
  };
};
